import React, { useEffect, useState } from "react";
// COMPONENTS
import FormSelectOpcCommand from "../../../../components/form/FormSelectOpcCommand";
import FormSelectOpcResponseContentType from "../../../../components/form/FormSelectOpcResponseContentType";
import FormSelectOpcReadContentType from "../../../../components/form/FormSelectOpcReadContentType";
import FormSelectOpcWriteContentType from "../../../../components/form/FormSelectOpcWriteContentType";
// COMPONENTS RESPONSE CONTENT TABLE
import ComponentReadItemResponse from "./responseContentTable/ComponentReadItemResponse";
import ComponentReadGroupResponse from "./responseContentTable/ComponentReadGroupResponse";
import ComponentGetNamesResponse from "./responseContentTable/ComponentGetNamesResponse";
import ComponentGetStatusResponse from "./responseContentTable/ComponentGetStatusResponse";
import ComponentGetServerListResponse from "./responseContentTable/ComponentGetServerListResponse";
import ComponentGetPropertyResponse from "./responseContentTable/ComponentGetPropertyResponse";
// COMPONENTS READ CONTENT TABLE
import ComponentReadSyncItemMessage from "./readContentTable/ComponentReadSyncItemMessage";
import ComponentReadSyncGroup from "./readContentTable/ComponentReadSyncGroup";
import ComponentEnableAsync from "./readContentTable/ComponentEnableAsync";
import ComponentReadAsyncGroup from "./readContentTable/ComponentReadAsyncGroup";
import ComponentReadAsyncItem from "./readContentTable/ComponentReadAsyncItem";
// COMPONENTS WRITE CONTENT TABLE
import ComponentWriteAddItem from "./writeContentTable/ComponentWriteAddItem";
import ComponentAddItems from "./writeContentTable/ComponentAddItems";
import ComponentAddReadWriteRelationship from "./writeContentTable/ComponentAddReadWriteRelationship";
import ComponentConnect from "./writeContentTable/ComponentConnect";
import ComponentMakeGroup from "./writeContentTable/ComponentMakeGroup";
import ComponentSetHostName from "./writeContentTable/ComponentSetHostName";
import ComponentWrite from "./writeContentTable/ComponentWrite";
// INTERFACES
import {
	OpcCommandType,
	ReadItemResponseInterface,
	GetStatusResponseInterface,
	GetServerListResponseInterface,
	GetPropertyResponseInterface,
	ReadGroupResponseInterface,
	GetNamesResponseInterface,
	ResponseContentTypesInterface,
	ReadContentTypesInterface,
	ReadSyncItemMessageInterface,
	ReadSyncGroupInterface,
	EnableAsyncInterface,
	ReadAsyncGroupInterface,
	ReadAsyncItemInterface,
	MakeGroupInterface,
	AddItemInterface,
	AddItemsInterface,
	WriteInterface,
	SetHostNameInterface,
	ConnectInterface,
	AddReadWriteRelationshipInterface,
	WriteContentTypesInterface,
	ResponseContentTableInterface,
	ReadContentTableInterface,
	WriteContentTableInterface,
	OpcCommand,
} from "../../../../../interfaces/AdapterCdoOpc.interface";

export default function CdoOpcCommand({
	opcCommand,
	setOpcCommand,
}: {
	opcCommand: {
		command: OpcCommand;
		opcCommand: OpcCommandType;
		address: string;
		description: string;
		msg_id: string;
		active: boolean;
		position: number;
		responseContentType: ResponseContentTypesInterface;
		readContentType: ReadContentTypesInterface;
		writeContentType: WriteContentTypesInterface;
	};
	setOpcCommand: (newValue: {
		command: OpcCommand;
		opcCommand: OpcCommandType;
		address: string;
		description: string;
		msg_id: string;
		active: boolean;
		position: number;
		responseContentType: ResponseContentTypesInterface;
		readContentType: ReadContentTypesInterface;
		writeContentType: WriteContentTypesInterface;
	}) => void;
}) {
	// ************************************************ */
	// GLOBAL VARIABLES ******************************* */

	// ************************************************ */
	// USE STATE VARIABLES **************************** */
	// RESPONSE CONTENT TABLE
	const [readItemResponse, setReadItemResponse] = useState<ReadItemResponseInterface>({
		data: { u16: 1 },
		item_name: "",
		group_name: "",
		quality: 0,
	});
	const [readGroupResponse, setReadGroupResponse] = useState<ReadGroupResponseInterface>({
		responses: [],
	});
	const [getNamesResponse, setGetNamesResponse] = useState<GetNamesResponseInterface>({ names: [] });
	const [getStatusResponse, setGetStatusResponse] = useState<GetStatusResponseInterface>({
		status: {
			vendor_info: "",
			start_time: {
				low_date_time: 0,
				high_date_time: 0,
			},
			current_time: {
				low_date_time: 0,
				high_date_time: 0,
			},
			last_update_time: {
				low_date_time: 0,
				high_date_time: 0,
			},
			group_count: 0,
			bandwidth: 0,
			major_version: 0,
			minor_version: 0,
			build_number: 0,
			server_status: "OPC_STATUS_RUNNING",
		},
	});
	const [getServerListResponse, setGetServerListResponse] = useState<GetServerListResponseInterface>({
		class_id_list: [],
		name_list: [],
	});
	const [getPropertyResponse, setGetPropertyResponse] = useState<GetPropertyResponseInterface>({
		property: {
			data: { u16: 0 },
			description: "",
			id: 0,
			type: 0,
		},
	});
	// READ CONTENT TABLE
	const [readSyncItemMessage, setReadSyncItemMessage] = useState<ReadSyncItemMessageInterface>({
		message: {
			group_name: "",
			item_name: "",
		},
	});
	const [readSyncGroup, setReadSyncGroup] = useState<ReadSyncGroupInterface>({ group_name: "" });
	const [enableAsync, setEnableAsync] = useState<EnableAsyncInterface>({ group_name: "" });
	const [readAsyncGroup, setReadAsyncGroup] = useState<ReadAsyncGroupInterface>({ group_name: "" });
	const [readAsyncItem, setReadAsyncItem] = useState<ReadAsyncItemInterface>({
		message: {
			group_name: "",
			item_name: "",
		},
	});
	// WRITE CONTENT TABLE
	const [makeGroup, setMakeGroup] = useState<MakeGroupInterface>({
		group: {
			name: "",
			update_rate: 0,
			revised_update_rate: 0,
			dead_band: 0,
			active: true,
		},
	});
	const [addItem, setAddItem] = useState<AddItemInterface>({
		item: {
			name: "",
			group_name: "",
			data_type: "i16",
		},
	});
	const [addItems, setAddItems] = useState<AddItemsInterface>({ group_name: "", item_names: [], data_types: [] });
	const [write, setWrite] = useState<WriteInterface>({ value: { u16: 0 }, group_name: "", item_name: "" });
	const [hostName, setHostName] = useState<SetHostNameInterface>({ host_name: "" });
	const [connect, setConnect] = useState<ConnectInterface>({ name: "" });
	const [addReadWriteRelationship, setAddReadWriteRelationship] = useState<AddReadWriteRelationshipInterface>({
		read: { value: { u16: 0 }, group_name: "", item_name: "" },
		write: { value: { u16: 0 }, group_name: "", item_name: "" },
	});

	// ************************************************ */
	// REDUX SLICE VARIABLES ************************** */

	// ************************************************ */
	// CUSTOM HOOKS *********************************** */

	// ************************************************ */
	// SERVICES AND API CALLS ************************* */

	// ************************************************ */
	// FUNCTIONS ************************************** */
	const onChangeCommandOpcCmdToMsg = (newValue: OpcCommandType) => {
		const copy = structuredClone(opcCommand);
		copy.opcCommand = newValue;
		setOpcCommand(copy);
	};

	const onChangeCommandResponseContentType = (newValue: ResponseContentTypesInterface) => {
		const copy = structuredClone(opcCommand);
		copy.responseContentType = newValue;
		setOpcCommand(copy);
	};

	const onChangeCommandReadContentType = (newValue: ReadContentTypesInterface) => {
		const copy = structuredClone(opcCommand);
		copy.readContentType = newValue;
		setOpcCommand(copy);
	};

	const onChangeCommandWriteContentType = (newValue: WriteContentTypesInterface) => {
		const copy = structuredClone(opcCommand);
		copy.writeContentType = newValue;
		setOpcCommand(copy);
	};

	// ************************************************ */
	// USE EFFECT ************************************* */
	useEffect(() => {
		let responseContent: ResponseContentTableInterface = { content: { read_item_response: readItemResponse } };
		if (opcCommand.opcCommand === "response_content_table") {
			if (opcCommand.responseContentType === "read_group_response") {
				responseContent = { content: { read_group_response: readGroupResponse } };
			} else if (opcCommand.responseContentType === "get_status_response") {
				responseContent = { content: { get_status_response: getStatusResponse } };
			} else if (opcCommand.responseContentType === "get_names_response") {
				responseContent = { content: { get_names_response: getNamesResponse } };
			} else if (opcCommand.responseContentType === "get_property_response") {
				responseContent = { content: { get_property_response: getPropertyResponse } };
			} else if (opcCommand.responseContentType === "get_server_list_response") {
				responseContent = { content: { get_server_list_response: getServerListResponse } };
			}
			const copy = structuredClone(opcCommand);
			copy.command = { response_content_table: responseContent };
			setOpcCommand(copy);
		}
	}, [
		readItemResponse,
		readGroupResponse,
		getNamesResponse,
		getStatusResponse,
		getServerListResponse,
		getPropertyResponse,
	]);

	useEffect(() => {
		let responseContent: ReadContentTableInterface = { content: { read_async_item: readSyncItemMessage } };
		if (opcCommand.opcCommand === "read_content_table") {
			if (opcCommand.readContentType === "read_sync_item_message") {
				responseContent = { content: { read_sync_item_message: readSyncItemMessage } };
			} else if (opcCommand.readContentType === "read_sync_group") {
				responseContent = { content: { read_sync_group: readSyncGroup } };
			} else if (opcCommand.readContentType === "enable_async") {
				responseContent = { content: { enable_async: enableAsync } };
			} else if (opcCommand.readContentType === "read_async_group") {
				responseContent = { content: { read_async_group: readAsyncGroup } };
			} else if (opcCommand.readContentType === "read_async_item") {
				responseContent = { content: { read_async_item: readAsyncItem } };
			}
			const copy = structuredClone(opcCommand);
			copy.command = { read_content_table: responseContent };
			setOpcCommand(copy);
		}
	}, [readSyncItemMessage, readSyncGroup, enableAsync, readAsyncGroup, readAsyncItem]);

	useEffect(() => {
		let responseContent: WriteContentTableInterface = { content: { make_group: makeGroup } };
		if (opcCommand.opcCommand === "write_content_table") {
			if (opcCommand.writeContentType === "make_group") {
				responseContent = { content: { make_group: makeGroup } };
			} else if (opcCommand.writeContentType === "add_item") {
				responseContent = { content: { add_item: addItem } };
			} else if (opcCommand.writeContentType === "add_items") {
				responseContent = { content: { add_items: addItems } };
			} else if (opcCommand.writeContentType === "write") {
				responseContent = { content: { write } };
			} else if (opcCommand.writeContentType === "set_host_name") {
				responseContent = { content: { set_host_name: hostName } };
			} else if (opcCommand.writeContentType === "connect") {
				responseContent = { content: { connect } };
			} else if (opcCommand.writeContentType === "add_read_write_relationship") {
				responseContent = { content: { add_read_write_relationship: addReadWriteRelationship } };
			}
			const copy = structuredClone(opcCommand);
			copy.command = { write_content_table: responseContent };
			setOpcCommand(copy);
		}
	}, [makeGroup, addItem, addItems, write, hostName, connect, addReadWriteRelationship]);

	// ************************************************ */
	// COMPONENT ************************************** */
	return (
		<div>
			<FormSelectOpcCommand
				inputValue={opcCommand.opcCommand}
				inputOnChange={onChangeCommandOpcCmdToMsg}
				disabledInput={false}
				isValue={false}
			/>
			{opcCommand.opcCommand === "response_content_table" && (
				<>
					<FormSelectOpcResponseContentType
						inputValue={opcCommand.responseContentType}
						inputOnChange={onChangeCommandResponseContentType}
						disabledInput={false}
					/>
					{opcCommand.responseContentType === "read_item_response" && (
						<ComponentReadItemResponse data={readItemResponse} setData={setReadItemResponse} />
					)}
					{opcCommand.responseContentType === "read_group_response" && (
						<ComponentReadGroupResponse data={readGroupResponse} setData={setReadGroupResponse} />
					)}
					{opcCommand.responseContentType === "get_names_response" && (
						<ComponentGetNamesResponse data={getNamesResponse} setData={setGetNamesResponse} />
					)}
					{opcCommand.responseContentType === "get_status_response" && (
						<ComponentGetStatusResponse data={getStatusResponse} setData={setGetStatusResponse} />
					)}
					{opcCommand.responseContentType === "get_server_list_response" && (
						<ComponentGetServerListResponse data={getServerListResponse} setData={setGetServerListResponse} />
					)}
					{opcCommand.responseContentType === "get_property_response" && (
						<ComponentGetPropertyResponse data={getPropertyResponse} setData={setGetPropertyResponse} />
					)}
				</>
			)}
			{opcCommand.opcCommand === "read_content_table" && (
				<>
					<FormSelectOpcReadContentType
						inputValue={opcCommand.readContentType}
						inputOnChange={onChangeCommandReadContentType}
						disabledInput={false}
					/>
					{opcCommand.readContentType === "read_sync_item_message" && (
						<ComponentReadSyncItemMessage data={readSyncItemMessage} setData={setReadSyncItemMessage} />
					)}
					{opcCommand.readContentType === "read_sync_group" && (
						<ComponentReadSyncGroup data={readSyncGroup} setData={setReadSyncGroup} />
					)}
					{opcCommand.readContentType === "enable_async" && (
						<ComponentEnableAsync data={enableAsync} setData={setEnableAsync} />
					)}
					{opcCommand.readContentType === "read_async_group" && (
						<ComponentReadAsyncGroup data={readAsyncGroup} setData={setReadAsyncGroup} />
					)}
					{opcCommand.readContentType === "read_async_item" && (
						<ComponentReadAsyncItem data={readAsyncItem} setData={setReadAsyncItem} />
					)}
				</>
			)}
			{opcCommand.opcCommand === "write_content_table" && (
				<>
					<FormSelectOpcWriteContentType
						inputValue={opcCommand.writeContentType}
						inputOnChange={onChangeCommandWriteContentType}
						disabledInput={false}
					/>
					{opcCommand.writeContentType === "make_group" && (
						<ComponentMakeGroup data={makeGroup} setData={setMakeGroup} />
					)}
					{opcCommand.writeContentType === "add_item" && <ComponentWriteAddItem data={addItem} setData={setAddItem} />}
					{opcCommand.writeContentType === "add_items" && <ComponentAddItems data={addItems} setData={setAddItems} />}
					{opcCommand.writeContentType === "write" && <ComponentWrite data={write} setData={setWrite} />}
					{opcCommand.writeContentType === "set_host_name" && (
						<ComponentSetHostName data={hostName} setData={setHostName} />
					)}
					{opcCommand.writeContentType === "connect" && <ComponentConnect data={connect} setData={setConnect} />}
					{opcCommand.writeContentType === "add_read_write_relationship" && (
						<ComponentAddReadWriteRelationship data={addReadWriteRelationship} setData={setAddReadWriteRelationship} />
					)}
				</>
			)}
		</div>
	);
}
